import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { LocalizedString } from '../../models/serializable/localized-string.js';
import { PhotoReference } from '../../models/serializable/file-reference/photo-reference.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';

export class LegacyBookNowComponent extends RenderableComponent {
  static readonly TYPE_ID: string = 'c124450cb61d48beab0d84067cc717ca';
  static readonly SCHEMA_VERSION: number = 3;
  static readonly DEFAULT_RENDER_ORDER: number = 0;

  constructor(
    public renderOrder: number,
    public isRenderingEnabled: boolean,
    public readonly businessId: string | null = null,
    public readonly photo: PhotoReference | null,
    public readonly titles: LocalizedString,
    public readonly descriptions: LocalizedString,
  ) {
    super();
  }

  get $removable(): boolean {
    return true;
  }

  get $typeId(): string {
    return LegacyBookNowComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(2, LegacyBookNowComponent.SCHEMA_VERSION);
  }

  static empty(renderOrder: number = this.DEFAULT_RENDER_ORDER): LegacyBookNowComponent {
    return new LegacyBookNowComponent(
        renderOrder,
        true,
        null,
        null,
        LocalizedString.empty(),
        LocalizedString.empty()
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyBookNowComponent {
    return new LegacyBookNowComponent(
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
        MapUtils.getOrNull<string>(map, 'businessId'),
        context.fromDataOrNull<PhotoReference>(MapUtils.getOrNull<SerializedData>(map, 'photo')),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'titles')),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'descriptions')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
      'businessId': this.businessId,
      'photo': context.toDataOrNull(this.photo),
      'titles': context.toData(this.titles),
      'descriptions': context.toData(this.descriptions),
    };
  }

  clone(): this {
    return new LegacyBookNowComponent(
        this.renderOrder,
        this.isRenderingEnabled,
        this.businessId,
        this.photo?.clone() ?? null,
        this.titles.clone(),
        this.descriptions.clone()
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.renderOrder === this.renderOrder &&
        other.isRenderingEnabled === this.isRenderingEnabled &&
        other.businessId === this.businessId &&
        isSameData(other.photo, this.photo) &&
        isSameData(other.titles, this.titles) &&
        isSameData(other.descriptions, this.descriptions);
  }
}

export class LegacyBookNowComponentBuilder extends DataModelBuilder<LegacyBookNowComponent> {
  build(context: SerializationContext, map: SerializedData): LegacyBookNowComponent {
    return LegacyBookNowComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LegacyBookNowComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyBookNowComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // Version 2:
    // - adds [renderOrder] field
    // - adds [isRenderingEnabled] field.
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', LegacyBookNowComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);

      version = 2;
    }

    // In this version `photo` of type [PhotoReference], `titles` of type
    // [LocalizedString] and `descriptions` of type [LocalizedString]
    // fields were introduced.
    if(version < 3) {
      MapUtils.setIfNullOrUndefined(map, 'titles', context.toData(LocalizedString.empty()));
      MapUtils.setIfNullOrUndefined(map, 'descriptions', context.toData(LocalizedString.empty()));

      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
